import { t } from "i18next"
import {
  type ChangeEvent,
  type FormEvent,
  useEffect,
  useRef,
  useState,
} from "react"

import {
  loginErrorKey,
  triggerLogin,
  useAuthError,
  useIsLoggingIn,
} from "@/App/Equities/shared/services/login"
import { isValidEmail } from "@/App/Equities/shared/utils"
import { ConnectionStatus, useConnectionStatus } from "@/services/connection"
import { runtimeConfig } from "@/services/runtimeConfig"

export const useLoginForm = () => {
  const authError = useAuthError()
  const loading = useIsLoggingIn()
  const connectionStatus = useConnectionStatus()

  const [email, setEmail] = useState(runtimeConfig.equitiesDefaultEmail)
  const [errorMessage, setErrorMessage] = useState("")
  const emailInputRef = useRef<HTMLInputElement>(null)

  useEffect(() => {
    setErrorMessage(authError ? t(loginErrorKey(authError)) : "")
    // the submit button is disabled while loading, which drops focus to <body>
    if (authError) {
      emailInputRef.current?.focus()
    }
  }, [authError])

  const handleEmailChange = (event: ChangeEvent<HTMLInputElement>) => {
    setEmail(event.target.value)
    setErrorMessage("")
  }

  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    const trimmedEmail = email.trim()

    if (!isValidEmail(trimmedEmail)) {
      setErrorMessage(t("login.invalidEmail"))
      emailInputRef.current?.focus()
      return
    }

    triggerLogin(trimmedEmail)
  }

  return {
    email,
    errorMessage,
    emailInputRef,
    loading,
    isDisconnected:
      connectionStatus === ConnectionStatus.DISCONNECTED ||
      connectionStatus === ConnectionStatus.IDLE_DISCONNECTED ||
      connectionStatus === ConnectionStatus.OFFLINE_DISCONNECTED,
    canSubmit:
      !!email && !loading && connectionStatus === ConnectionStatus.CONNECTED,
    handleEmailChange,
    handleSubmit,
  }
}
